import * as moment from "moment-timezone";

/**
 * Single source of truth for the "admin uploads an invoice on behalf of a
 * vendor" gate (GPXUSAP-1860). Framework-free by design so it can be unit
 * tested directly with vitest (this repo's karma/ng-test harness is broken).
 *
 * True only when the user holds the VendorInvoiceUploader permission AND the
 * uiConfig.adminUploadOnBehalf flag is strictly boolean true. Every call site
 * (sidebar entry, manage-vendors row action, etc.) must call this function —
 * no inline duplicated conditions.
 */
export function canUploadOnBehalf(
    permissions: string[] | null | undefined,
    uiFlag: unknown
): boolean {
    if (!Array.isArray(permissions)) {
        return false;
    }
    if (uiFlag !== true) {
        return false;
    }
    return permissions.includes("VendorInvoiceUploader");
}

/** Client-confirmed copy, verbatim — shown when a vendor has no portal user. */
export const UPLOAD_ON_BEHALF_NO_PORTAL_USER_MESSAGE =
    "Vendor has not activated portal access";

/** Shown when the vendor exists but onboarding hasn't completed. This is a
 * distinct reason from UPLOAD_ON_BEHALF_NO_PORTAL_USER_MESSAGE above — do
 * not conflate the two in the UI. */
export const UPLOAD_ON_BEHALF_ONBOARDING_INCOMPLETE_MESSAGE =
    "Vendor onboarding is not complete";

/**
 * Single source of truth for why the manage-vendors "Upload invoice" row
 * action is disabled. Returns the reason string to show as [disabled]/
 * tooltip, or null when the action should be enabled.
 *
 * `hasPortalUser` (GPXUSAP-1860 fix round 4) is served by `getSuppliers` on
 * the Node side (true when a `user` doc exists with `_id === supplierCode`)
 * — but that change ships as its own PR/deploy, separate from this one. A
 * MISSING `hasPortalUser` (older cached response, or an API not yet
 * redeployed) is treated as UNKNOWN, not as false: we fall back to today's
 * onboarding-only behaviour rather than blocking every vendor the moment a
 * stale response comes back.
 *
 * GPXUSAP-1860 fix round 5 (reverses round 4's precedence): onboarding is
 * checked FIRST. In production data, every not-yet-onboarded vendor also
 * lacks a portal user, so checking portal-access first made the onboarding
 * message effectively unreachable — every row showed "Vendor has not
 * activated portal access" even when the actionable problem was that
 * onboarding itself was incomplete. Onboarding-incomplete is the more
 * actionable reason for a vendor mid-onboarding; portal-access is reserved
 * for a vendor who IS onboarded but still has no login.
 */
export function uploadOnBehalfDisabledReason(
    vendor:
        | { has_on_boarded?: string; hasPortalUser?: boolean }
        | null
        | undefined
): string | null {
    const notOnboarded = vendor?.has_on_boarded !== "Completed";
    if (notOnboarded) {
        return UPLOAD_ON_BEHALF_ONBOARDING_INCOMPLETE_MESSAGE;
    }

    const noPortalUser = vendor?.hasPortalUser === false;
    if (noPortalUser) {
        return UPLOAD_ON_BEHALF_NO_PORTAL_USER_MESSAGE;
    }
    return null;
}

/** The buyer-reports colKey/columnItems `value`s that stay OPT-IN behind the
 * upload-on-behalf gate.
 *
 * GPXUSAP-1860 follow-up: `uploadedBy`/`uploadedAt` are no longer here. They
 * are now FIXED columns (rendered like Vendor Code / Barcode, gated directly
 * on `canUploadOnBehalf`) and are not offered in the Columns multiselect at
 * all, so there is nothing left for this list to strip. `uploadSource` is
 * still a selectable column, so it must stay.
 *
 * `uploadedAt` is a plain display STRING ("YYYY-MM-DD HH:mm:ss") written by
 * the uploader, NOT a Date/epoch — render it verbatim, never through a date
 * pipe/formatter (that is how GPXUSAP-1790 shipped a wrong-date bug). */
export const UPLOAD_ON_BEHALF_REPORT_COLUMN_KEYS = ["uploadSource"] as const;

/**
 * Filters the opt-in upload-on-behalf report columns (uploadSource) out
 * of a columnItems-shaped list unless the user can use the feature. Pure and
 * generic over the `{ value: string }` shape so it doesn't need to import the
 * component's column type.
 */
export function filterUploadOnBehalfColumns<T extends { value: string }>(
    columnItems: T[],
    canUploadOnBehalfValue: boolean
): T[] {
    if (canUploadOnBehalfValue) {
        return columnItems;
    }
    return columnItems.filter(
        (item) =>
            !(UPLOAD_ON_BEHALF_REPORT_COLUMN_KEYS as readonly string[]).includes(
                item.value
            )
    );
}

/**
 * Keeps a column selection honest: drops any colKey that is not (or no longer)
 * an offered column. `uploadedBy`/`uploadedAt` are in `defaultColumns` so they
 * render on page load, but `filterUploadOnBehalfColumns` strips them from
 * `columnItems` for an admin without the permission — without this filter the
 * default selection would still flip their `show_*` flags on and render the
 * columns the gate just removed.
 */
export function selectableColumnValues<T extends { value: string }>(
    columnItems: T[],
    selectedValues: string[] | null | undefined
): string[] {
    const offered = new Set(columnItems.map((item) => item.value));
    return (selectedValues || []).filter((value) => offered.has(value));
}

/**
 * Builds the `YYYY-MM-DD~YYYY-MM-DD` value the Node `uploadedAtRange` filter
 * expects from the two datepicker bounds (already formatted YYYY-MM-DD by the
 * caller). A single bound is mirrored onto the other end so a From-only or
 * To-only selection still yields a closed range — same behaviour the Date
 * Submitted range already has. Returns "" when neither bound is set, which the
 * caller treats as "do not send this filter".
 *
 * rec.uploadedAt is a plain "YYYY-MM-DD HH:mm:ss" STRING in Mongo, so the Node
 * side appends " 00:00:00"/" 23:59:59" and compares lexicographically — the
 * bounds MUST be YYYY-MM-DD or the range silently matches nothing.
 */
export function uploadedAtRangeValue(
    from: string | null | undefined,
    to: string | null | undefined
): string {
    const start = from || to;
    const end = to || from;
    return start && end ? `${start}~${end}` : "";
}

/** GPXUSAP-2043: on-behalf daily report helpers. rec.uploadedAt is a UTC
 * "YYYY-MM-DD HH:mm:ss" string (prod JVM is UTC); the report works in IST. */
const IST = "Asia/Kolkata";
const UPLOADED_AT_FORMAT = "YYYY-MM-DD HH:mm:ss";
/** Same format as the buyer-reports grid (DateUtilsService.buyerReportDisplayFormat). */
const GRID_DISPLAY_FORMAT = "ddd MMM DD, HH:mm:ss z YYYY";

export type UploadSummary = { _id: string | null; eInvoice: number; dsInvoice: number; total: number };
export const ADMIN_ON_BEHALF_SOURCE = "admin_on_behalf";

export function invoiceCategoryLabel(f: string | undefined): string {
    return f === "e-invoice" ? "IRN e-invoice" : f === "ds-invoice" ? "Digitally Signed" : "-";
}

export function uploadSummaryRows(rows: UploadSummary[] = []) {
    const out = rows.map((r) => ({ uploader: r._id || "-", eInvoice: r.eInvoice, dsInvoice: r.dsInvoice, total: r.total }));
    const sum = (k: "eInvoice" | "dsInvoice" | "total") => out.reduce((a, r) => a + r[k], 0);
    return rows.length
        ? [...out, { uploader: "Total", eInvoice: sum("eInvoice"), dsInvoice: sum("dsInvoice"), total: sum("total") }]
        : [];
}

export function uploadSummaryCsv(rows: UploadSummary[]): string {
    const esc = (v: unknown) => (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
    return [
        "Uploader ID,IRN e-invoice,Digitally Signed,Total",
        ...uploadSummaryRows(rows).map((r) => [r.uploader, r.eInvoice, r.dsInvoice, r.total].map(esc).join(",")),
    ].join("\n");
}

/** Today in Asia/Kolkata as "YYYY-MM-DD~YYYY-MM-DD" (not the browser's local date). */
export function istTodayRange(now: moment.Moment = moment()): string {
    const d = now.clone().tz(IST).format("YYYY-MM-DD");
    return `${d}~${d}`;
}

/** IST day range "YYYY-MM-DD~YYYY-MM-DD" -> UTC datetime bounds for Node uploadedAtRange. */
export function istDayRangeToUtc(range: string): string {
    const [from, to] = (range || "").split("~");
    if (!from || !to) {
        return "";
    }
    const a = moment.tz(from, "YYYY-MM-DD", true, IST).startOf("day").utc();
    const b = moment.tz(to, "YYYY-MM-DD", true, IST).endOf("day").utc();
    return `${a.format(UPLOADED_AT_FORMAT)}~${b.format(UPLOADED_AT_FORMAT)}`;
}

/** Parses the UTC rec.uploadedAt string; null when blank or unparseable. */
export function parseUtcUploadedAt(s: string | null | undefined): moment.Moment | null {
    const m = s ? moment.utc(s, UPLOADED_AT_FORMAT, true) : null;
    return m && m.isValid() ? m : null;
}

/** UTC rec.uploadedAt -> IST in the grid's display format. Blank -> "", unparseable -> raw. */
export function utcToIstDisplay(s: string | null | undefined): string {
    const m = parseUtcUploadedAt(s);
    return m ? m.tz(IST).format(GRID_DISPLAY_FORMAT) : s || "";
}
